import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraProgramSidebar, SpecraSidebarBackLink } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { renderRouterLink } from '../lib/renderRouterLink';
import { PRIMARY_DEFS, useProgramSections } from './programSections';
import { SidebarUserMenu } from './SidebarUserMenu';
const FAVORITES_KEY = 'specra-program-nav-favorites';
function loadFavorites() {
    try {
        const raw = window.localStorage.getItem(FAVORITES_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((v)=>typeof v === 'string') : [];
    } catch  {
        return [];
    }
}
export function ProgramSidebar({ programId }) {
    const base = `/delivery/programs/${programId}`;
    const location = useLocation();
    const { user } = useAuth();
    const [desktopCollapsed, setCollapsed] = useState(false);
    const isNarrow = useMediaQuery('(max-width: 767px)');
    const collapsed = isNarrow || desktopCollapsed;
    const [favorites, setFavorites] = useState(loadFavorites);
    const [groupState, setGroupState] = useState({});
    // ⌘\ / Ctrl+\ toggles the rail (LAYOUT-SPEC §7).
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === '\\' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                setCollapsed((value)=>!value);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, []);
    // Favorites persist locally (plan decision: localStorage, no backend).
    useEffect(()=>{
        try {
            window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
        } catch  {
        // Storage unavailable (private mode) — favorites stay session-local.
        }
    }, [
        favorites
    ]);
    const togglePin = useCallback((id)=>{
        setFavorites((current)=>current.includes(id) ? current.filter((v)=>v !== id) : [
                ...current,
                id
            ]);
    }, []);
    const { groups: visibleGroups, programName, contractNumber } = useProgramSections(programId);
    const isActive = useCallback((def)=>{
        if (def.path === '') return location.pathname === base;
        const href = `${base}/${def.path}`;
        return location.pathname === href || location.pathname.startsWith(`${href}/`);
    }, [
        base,
        location.pathname
    ]);
    const toItem = useCallback((def)=>({
            id: def.id,
            label: def.label,
            icon: def.icon,
            href: def.path === '' ? base : `${base}/${def.path}`,
            active: isActive(def),
            testId: def.testId,
            pinnable: def.pinnable !== false,
            pinned: favorites.includes(def.id),
            onTogglePin: ()=>togglePin(def.id),
            pinTestId: Testids.programNav.pin
        }), [
        base,
        favorites,
        isActive,
        togglePin
    ]);
    const groups = useMemo(()=>{
        const defsById = new Map(visibleGroups.flatMap((g)=>g.items).map((d)=>[
                d.id,
                d
            ]));
        // Pinned items duplicate into the top ungrouped section (mockup 3218–3220);
        // the duplicate carries its own testid so journeys can target it without
        // colliding with the original item's selector.
        const pinnedItems = favorites.map((id)=>defsById.get(id)).filter((def)=>def != null).map((def)=>({
                ...toItem(def),
                id: `pinned-${def.id}`,
                testId: Testids.programNav.pinnedItem
            }));
        return [
            {
                id: 'primary',
                items: [
                    ...PRIMARY_DEFS.map(toItem),
                    ...pinnedItems
                ]
            },
            ...visibleGroups.map((group)=>({
                    id: group.id,
                    label: group.label,
                    items: group.items.map(toItem)
                }))
        ];
    }, [
        favorites,
        visibleGroups,
        toItem
    ]);
    const defaultOpenById = useMemo(()=>new Map(visibleGroups.map((group)=>[
                group.id,
                group.defaultOpen
            ])), [
        visibleGroups
    ]);
    // Effective open state: an explicit user toggle wins, else the group's
    // default — and a group holding the active route is never hidden by default.
    const isGroupOpen = (group)=>groupState[group.id] ?? (defaultOpenById.get(group.id) === true || group.items.some((item)=>item.active));
    return /*#__PURE__*/ _jsx(SpecraProgramSidebar, {
        backAction: /*#__PURE__*/ _jsx(SpecraSidebarBackLink, {
            collapsed: collapsed,
            href: "/delivery/programs",
            label: "All programs",
            renderLink: renderRouterLink,
            testId: Testids.programNav.back
        }),
        className: "h-full max-md:max-h-none max-md:w-[var(--component-sidebar-collapsed-width)]",
        collapsed: collapsed,
        collapseToggleTestId: Testids.programNav.collapse,
        groups: groups,
        footer: user ? /*#__PURE__*/ _jsx(SidebarUserMenu, {
            collapsed: collapsed,
            user: user
        }) : undefined,
        getGroupProps: (group)=>group.label ? {
                collapsible: true,
                containsActive: group.items.some((item)=>item.active),
                labelTestId: Testids.programNav.groupLabel,
                onToggle: ()=>setGroupState((state)=>({
                            ...state,
                            [group.id]: !isGroupOpen(group)
                        })),
                open: isGroupOpen(group)
            } : {},
        navigationLabel: "Program navigation",
        navigationTestId: Testids.programNav.sidebar,
        onCollapseToggle: isNarrow ? undefined : ()=>setCollapsed((value)=>!value),
        programId: contractNumber,
        programName: programName,
        renderLink: renderRouterLink
    });
}
